import AgentPagesResource from 'Resources/AgentPages';
import AppService from 'App/legacy/AppService';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import StateRouter from 'App/services/StateRouter';

const tryGoToPage = async (page: string, opts = {}) => {
	StateRouter.go(page, opts);
};

// The `agentPageView` ui-router state always resolves (it just mounts the React
// host), so $state.go can't tell us a Page is gone — the 404 only surfaces
// inside the iframe. Resolve the page by id (stored in userParams.startPage)
// with a direct getOne — the list endpoint caps at 100, so a list scan would
// wrongly fall back for pages beyond that. Reject when the page is missing or
// not active so the caller's fallback chain kicks in instead of stranding the
// user on an error screen. Returns the slug, which is what the route needs.
const resolveAgentPageSlug = async (id: number): Promise<string> => {
	// A missing or forbidden page rejects the GET — map that to the
	// not-available fallback so the caller's fallback chain kicks in.
	const res = id ? await AgentPagesResource.get(id).catch(() => null) : null;
	const page = res?.data ?? null;
	if (!page || page.status !== 'active') {
		throw new Error(`Agent page ${id} is not available`);
	}
	return page.slug;
};

const transformStartPage = (page: string, startPageStates: any) => {
	return startPageStates.hasOwnProperty(page) ? startPageStates[page as keyof typeof startPageStates] : page;
};

// AI Workspace pages are saved as `page:<id>` in userParams.startPage so the
// single string field can carry the page id without colliding with the existing
// react-root-* values. Kept in sync with admin/pages/profile.jsx.
const AGENT_PAGE_PREFIX = 'page:';

export const goToStartPage = () => {
	if (AppService.getMetadata().onboarding?.length) {
		StateRouter.go('react-root-onboarding', { openFullScreen: true });
	} else {
		/*
			Has two-way state conversions between angular and react views just to be safe in case of releasing/unreleasing flags.
			The start page should be set correctly in admin/profile.jsx as long as they save it.
			When releasing a new port, we should probably update everyones startPage accordingly.
		*/
		const taskList = 'react-root-task';

		const startPageStates = {
			todo: taskList,

			activities:
				hasSoftDeployAccess('REMOVE_ACTIVITIES') && hasSoftDeployAccess('TODO_LIST') ? taskList : 'activities',

			accounts: 'react-root-companies',

			'react-root-companies': 'react-root-companies',

			reports: 'react-root-reportcenter',

			analytics: 'analytics',

			looker: 'looker',

			groupMail: 'react-root-mail-campaigns',

			socialEvents: 'react-root-events',

			landingPages: 'react-root-landingpages',
			'react-root-landingpages': 'react-root-landingpages',

			formSubmits: 'react-root-formSubmits',

			esign: 'esign',

			mailTemplates: 'react-root-mailtemplates',
			'react-root-mailtemplates': 'react-root-mailtemplates',

			contacts: 'react-root-contacts',
			'react-root-contacts': 'react-root-contacts',

			orders: 'react-root-orders',
			'react-root-orders': 'react-root-orders',

			salesboard: 'react-root-salesboard',
			'react-root-salesboard': 'react-root-salesboard',

			portfolio: 'react-root-customerPortfolio',
			'react-root-customerPortfolio': 'react-root-customerPortfolio',

			listMail: 'react-root-mail',
			'react-root-mail': 'react-root-mail',

			agreements: 'react-root-agreements',
			'react-root-agreements': 'react-root-agreements',

			leads2: 'react-root-leads',
			'react-root-leads': 'react-root-leads',

			campaigns: 'react-root-campaigns',
			'react-root-campaigns': 'react-root-campaigns',

			listForms: 'react-root-forms',
			'react-root-forms': 'react-root-forms',

			visitors: 'react-root-visitors',
			'react-root-visitors': 'react-root-visitors',

			support: 'react-root-support',

			projectBoard: 'react-root-projectboard'
		};
		const self = AppService.getSelf();
		const accountSelf = AppService.getAccountSelf();
		const isSupportAndNotCRM = self.support && !self.crm;
		const isServiceAndNotCRM = self.service && !self.crm;
		const fallbackPages = [
			isSupportAndNotCRM
				? transformStartPage('support', startPageStates)
				: isServiceAndNotCRM
				? transformStartPage('react-root-projectBoard', startPageStates)
				: accountSelf?.products.crm
				? transformStartPage('activities', startPageStates)
				: transformStartPage('leads2', startPageStates),
			'react-root-salesboard'
		];

		let savedStartPage = self.userParams.startPage || fallbackPages[0];

		const validSupportPages = ['react-root-support', 'react-root-companies', 'react-root-contacts'];
		if (isSupportAndNotCRM && !validSupportPages.includes(savedStartPage)) {
			// Quick and ugly fix, this whole thing is a mess (fallback doesn't work as there is no error to catch from tryGoToPage)
			savedStartPage = validSupportPages[0];
		}

		if (savedStartPage.startsWith(AGENT_PAGE_PREFIX)) {
			const id = Number(savedStartPage.slice(AGENT_PAGE_PREFIX.length));
			resolveAgentPageSlug(id)
				.then(slug => tryGoToPage('react-root-agentPageView', { slug }))
				.catch(() => tryGoToPage(fallbackPages[0]))
				.catch(() => tryGoToPage(fallbackPages[1]));
			return;
		}

		const transformedStartPage = transformStartPage(savedStartPage, startPageStates);

		tryGoToPage(transformedStartPage)
			.catch(() => tryGoToPage(fallbackPages[0]))
			.catch(() => tryGoToPage(fallbackPages[1]));
	}
};
